import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Lock, UserX, Shield, User, BookOpen } from 'lucide-react';
import api from '../api/client';
import { useAuthStore } from '../store/auth';
import toast from 'react-hot-toast';
import Modal from '../components/ui/Modal';
import FormField, { Input, Select, FormRow, SubmitBtn } from '../components/ui/FormField';

const ROLE_COLORS: any = {
  admin:     { bg:'#1a0d2e', color:'#a78bfa', border:'#3a1d6e' },
  director:  { bg:'var(--accent-bg)', color:'var(--accent)', border:'var(--border-active)' },
  librarian: { bg:'var(--success-bg)', color:'var(--success)', border:'#0a4a2a' },
  staff:     { bg:'var(--warning-bg)', color:'var(--warning)', border:'#4a3010' },
};

export default function StaffPage() {
  const qc = useQueryClient();
  const { user: currentUser } = useAuthStore();
  const currentRole = currentUser?.role || 'librarian';

  const [showNew, setShowNew] = useState(false);
  const [editUser, setEditUser] = useState<any>(null);
  const [pwdUser, setPwdUser] = useState<any>(null);
  const [newPwd, setNewPwd] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ username:'', password:'', full_name:'', email:'', phone:'', role_id:'3', branch_id:'1' });

  const { data } = useQuery({ queryKey: ['users'], queryFn: () => api.get('/auth/users') } as any);
  const { data: rolesData } = useQuery({ queryKey: ['roles'], queryFn: () => api.get('/auth/roles') } as any);
  const { data: branchesData } = useQuery({ queryKey: ['branches'], queryFn: () => api.get('/branches') } as any);

  const users = (data as any)?.data?.users || [];
  const roles = (rolesData as any)?.data?.roles || [];
  const branches = (branchesData as any)?.data?.branches || [];
  const set = (k: string) => (e: any) => setForm(f => ({...f, [k]: e.target.value}));

  const canEdit = (u: any) => {
    if (currentRole === 'admin') return true;
    if (currentRole === 'director' && u.role !== 'admin' && u.id !== 1) return true;
    return false;
  };

  const availableRoles = currentRole === 'admin'
    ? roles
    : roles.filter((r: any) => r.name !== 'admin');

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.username || !form.password || !form.full_name) return toast.error('სავალდებულო ველები');
    setSaving(true);
    try {
      await api.post('/auth/users', form);
      toast.success('მომხმარებელი შეიქმნა!');
      qc.invalidateQueries({ queryKey: ['users'] });
      setShowNew(false);
      setForm({ username:'', password:'', full_name:'', email:'', phone:'', role_id:'3', branch_id:'1' });
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'შეცდომა');
    } finally { setSaving(false); }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/auth/users/${editUser.id}`, editUser);
      toast.success('განახლდა!');
      qc.invalidateQueries({ queryKey: ['users'] });
      setEditUser(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'შეცდომა');
    } finally { setSaving(false); }
  };

  const handlePwd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPwd.length < 6) return toast.error('მინიმუმ 6 სიმბოლო');
    setSaving(true);
    try {
      await api.put(`/auth/users/${pwdUser.id}/password`, { new_password: newPwd });
      toast.success('პაროლი შეიცვალა!');
      setPwdUser(null); setNewPwd('');
    } catch { toast.error('შეცდომა'); }
    finally { setSaving(false); }
  };

  const handleDeactivate = async (u: any) => {
    if (!window.confirm(`${u.full_name} გათიშვა?`)) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success('გათიშულია');
      qc.invalidateQueries({ queryKey: ['users'] });
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'შეცდომა');
    }
  };

  const ICONS: any = { admin: Shield, director: User, librarian: BookOpen, staff: User };

  return (
    <div style={{padding:24,fontFamily:'system-ui,sans-serif'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24}}>
        <div>
          <h1 style={{fontSize:20,fontWeight:600,color:'var(--text-primary)',margin:0}}>პერსონალი</h1>
          <p style={{fontSize:12,color:'var(--text-faint)',marginTop:4}}>მომხმარებლები და წვდომის მართვა</p>
        </div>
        <button onClick={() => setShowNew(true)}
          style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',background:'#1d4ed8',border:'none',borderRadius:8,color:'white',fontSize:12,fontWeight:500,cursor:'pointer'}}>
          <Plus size={15}/> ახალი მომხმარებელი
        </button>
      </div>

      {roles.map((role: any) => {
        const roleUsers = users.filter((u: any) => u.role === role.name);
        const Icon = ICONS[role.name] || User;
        const rc = ROLE_COLORS[role.name] || ROLE_COLORS.staff;
        return (
          <div key={role.id} style={{marginBottom:20}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
              <div style={{width:28,height:28,borderRadius:8,background:rc.bg,border:`1px solid ${rc.border}`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                <Icon size={14} style={{color:rc.color}} />
              </div>
              <span style={{fontSize:14,fontWeight:600,color:'var(--text-secondary)'}}>{role.name_ka}</span>
              <span style={{padding:'2px 8px',borderRadius:6,fontSize:11,background:rc.bg,color:rc.color,border:`1px solid ${rc.border}`}}>{roleUsers.length}</span>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:10}}>
              {roleUsers.length === 0 && (
                <div style={{background:'var(--bg-card)',border:'1px dashed #1e2d45',borderRadius:10,padding:16,textAlign:'center',color:'var(--text-dimmed)',fontSize:12}}>
                  მომხმარებელი არ არის
                </div>
              )}
              {roleUsers.map((u: any) => (
                <div key={u.id} style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:10,overflow:'hidden'}}>
                  <div style={{padding:'12px 14px',display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:40,height:40,borderRadius:10,background:rc.bg,border:`1px solid ${rc.border}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,fontWeight:700,color:rc.color,flexShrink:0}}>
                      {(u.full_name?.[0]||'?').toUpperCase()}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,color:'var(--text-primary)'}}>{u.full_name}</div>
                      <div style={{fontSize:11,color:'var(--text-faint)'}}>@{u.username}</div>
                    </div>
                    <span style={{padding:'2px 6px',borderRadius:5,fontSize:10,fontWeight:500,background:u.is_active?'var(--success-bg)':'var(--danger-bg)',color:u.is_active?'var(--success)':'var(--danger)'}}>
                      {u.is_active?'აქტიური':'გათიშული'}
                    </span>
                  </div>
                  <div style={{padding:'8px 14px',borderTop:'1px solid #0f1826'}}>
                    {u.email && <div style={{fontSize:11,color:'var(--text-faint)',marginBottom:2}}>✉ {u.email}</div>}
                    {u.phone && <div style={{fontSize:11,color:'var(--text-faint)',marginBottom:2}}>📞 {u.phone}</div>}
                    {u.branch_name && <div style={{fontSize:11,color:'var(--text-faint)'}}>🏛 {u.branch_name}</div>}
                  </div>
                  {canEdit(u) && (
                    <div style={{padding:'8px 14px',borderTop:'1px solid #0f1826',display:'flex',gap:6}}>
                      <button onClick={() => setEditUser({...u})}
                        style={{display:'flex',alignItems:'center',gap:4,padding:'5px 10px',background:'var(--bg-dark2)',border:'1px solid #1e2d45',borderRadius:6,fontSize:11,color:'var(--accent)',cursor:'pointer'}}>
                        <Edit size={11}/> რედაქტირება
                      </button>
                      <button onClick={() => { setPwdUser(u); setNewPwd(''); }}
                        style={{display:'flex',alignItems:'center',gap:4,padding:'5px 10px',background:'var(--bg-dark2)',border:'1px solid #1e2d45',borderRadius:6,fontSize:11,color:'var(--warning)',cursor:'pointer'}}>
                        <Lock size={11}/> პაროლი
                      </button>
                      {u.id !== 1 && (
                        <button onClick={() => handleDeactivate(u)}
                          style={{display:'flex',alignItems:'center',gap:4,padding:'5px 10px',background:'var(--danger-bg)',border:'1px solid #5a1a1a',borderRadius:6,fontSize:11,color:'var(--danger)',cursor:'pointer',marginLeft:'auto'}}>
                          <UserX size={11}/> გათიშვა
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <Modal open={showNew} onClose={() => setShowNew(false)} title="ახალი მომხმარებელი" width={500}>
        <form onSubmit={handleCreate}>
          <FormRow>
            <FormField label="სახელი გვარი" required>
              <Input value={form.full_name} onChange={set('full_name')} placeholder="სახელი გვარი" />
            </FormField>
            <FormField label="მომხმარებელი" required>
              <Input value={form.username} onChange={set('username')} placeholder="username" />
            </FormField>
          </FormRow>
          <FormField label="პაროლი" required>
            <Input type="password" value={form.password} onChange={set('password')} placeholder="მინიმუმ 6 სიმბოლო" />
          </FormField>
          <FormRow>
            <FormField label="ელ-ფოსტა">
              <Input type="email" value={form.email} onChange={set('email')} placeholder="mail@..." />
            </FormField>
            <FormField label="ტელეფონი">
              <Input value={form.phone} onChange={set('phone')} placeholder="+995..." />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="როლი" required>
              <Select value={form.role_id} onChange={set('role_id')}>
                {availableRoles.map((r: any) => <option key={r.id} value={r.id}>{r.name_ka}</option>)}
              </Select>
            </FormField>
            <FormField label="ფილიალი">
              <Select value={form.branch_id} onChange={set('branch_id')}>
                {branches.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </Select>
            </FormField>
          </FormRow>
          <SubmitBtn loading={saving} label="შექმნა" />
        </form>
      </Modal>

      <Modal open={!!editUser} onClose={() => setEditUser(null)} title="რედაქტირება" width={500}>
        {editUser && (
          <form onSubmit={handleEdit}>
            <FormField label="სახელი გვარი" required>
              <Input value={editUser.full_name||''} onChange={e => setEditUser((u: any) => ({...u,full_name:e.target.value}))} />
            </FormField>
            <FormRow>
              <FormField label="ელ-ფოსტა">
                <Input type="email" value={editUser.email||''} onChange={e => setEditUser((u: any) => ({...u,email:e.target.value}))} />
              </FormField>
              <FormField label="ტელეფონი">
                <Input value={editUser.phone||''} onChange={e => setEditUser((u: any) => ({...u,phone:e.target.value}))} />
              </FormField>
            </FormRow>
            <FormRow>
              <FormField label="როლი">
                <Select value={String(editUser.role_id||3)} onChange={e => setEditUser((u: any) => ({...u,role_id:parseInt(e.target.value)}))}>
                  {availableRoles.map((r: any) => <option key={r.id} value={r.id}>{r.name_ka}</option>)}
                </Select>
              </FormField>
              <FormField label="სტატუსი">
                <Select value={String(editUser.is_active)} onChange={e => setEditUser((u: any) => ({...u,is_active:parseInt(e.target.value)}))}>
                  <option value="1">აქტიური</option>
                  <option value="0">გათიშული</option>
                </Select>
              </FormField>
            </FormRow>
            <SubmitBtn loading={saving} label="შენახვა" />
          </form>
        )}
      </Modal>

      <Modal open={!!pwdUser} onClose={() => setPwdUser(null)} title={`პაროლი — ${pwdUser?.full_name}`} width={400}>
        <form onSubmit={handlePwd}>
          <FormField label="ახალი პაროლი" required>
            <Input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} placeholder="მინიმუმ 6 სიმბოლო" autoFocus />
          </FormField>
          <SubmitBtn loading={saving} label="შეცვლა" />
        </form>
      </Modal>
    </div>
  );
}
